/**
 * Rich coaching copy by synthetic week kind × intensity.
 * `match` on guidance rows: EASY_RUN | WALK | LONG_OR_SAT | TEMPO | INTERVAL | SHAKEOUT | CROSS_TRAINING
 */
/** Same copy for every week kind × intensity when Cross Training is on the schedule. */
export const CROSS_TRAINING_GUIDANCE = {
  match: "CROSS_TRAINING",
  text: "Cross Training ({duration} min): Low impact cardio to support your running without adding stress to your legs. Swimming, cycling, the elliptical, or yoga all work well. Keep effort easy and enjoy the break from the pavement.",
};

export const COACHING_LIBRARY = {
  ONBOARDING: {
    Easy: {
      title: "Welcome to your first week",
      body: [
        "This week is simple: just show up. Don't worry about pace or performance. Finish each run feeling like you could have done a little more. That's exactly where you want to be.",
        "Your body is getting used to a new routine. Tendons, joints, and connective tissue take longer to adapt than your cardio fitness does. Keeping it easy now protects you from the most common beginner mistake: doing too much too soon.",
      ],
      guidance: [
        {
          match: "EASY_RUN",
          text: "Easy Run: Run at a pace where you can hold a full conversation. If you can't speak in full sentences, slow down. Walk breaks are totally fine. Target effort: 3 out of 10.",
        },
        {
          match: "WALK",
          text: "Walk: Active recovery. Walk briskly and comfortably. Gentle movement helps your legs bounce back faster than sitting still.",
        },
        CROSS_TRAINING_GUIDANCE,
      ],
      recovery:
        "Sleep is your best friend this week. Aim for 7 to 9 hours. Some muscle soreness is normal but it should feel like fatigue, not sharp pain. Stay hydrated and don't skip your rest days.",
    },
    Moderate: {
      title: "Setting the foundation",
      body: [
        "This week is intentionally manageable. Even if it feels easy, don't push harder. Every run should feel controlled and comfortable. You're not building fitness yet, you're building the habit.",
        "Pay attention to how your body responds to the schedule. Noticing which days feel fresh versus heavy is useful information for the weeks ahead.",
      ],
      guidance: [
        {
          match: "EASY_RUN",
          text: "Easy Run: Conversational pace throughout. You should be able to speak several words comfortably between breaths. RPE 4 out of 10.",
        },
        {
          match: "LONG_OR_SAT",
          text: "Long Run: Same effort as your easy runs, just longer. No pace target. The goal is time on feet.",
        },
        CROSS_TRAINING_GUIDANCE,
      ],
      recovery:
        "Don't underestimate sleep and nutrition this week. Eat a solid meal within 45 minutes of your longer runs. Some soreness in the first few days is normal and easy movement helps more than sitting on the couch.",
    },
    Challenging: {
      title: "Controlled start, big weeks ahead",
      body: [
        "You have a demanding plan coming. That makes this week more important, not less. Use it to establish good habits: running by effort, not ego, and treating easy days as genuinely easy.",
        "The temptation for experienced runners is to run every day too hard. Resist it. Your easy runs should feel almost embarrassingly slow. That's correct. You'll need fresh legs when quality work starts next week.",
      ],
      guidance: [
        {
          match: "EASY_RUN",
          text: "Easy Run: RPE 4 out of 10. Full sentences, not just short phrases. If you're working to speak, you're going too fast.",
        },
        {
          match: "LONG_OR_SAT",
          text: "Long Run: Easy effort, relaxed form. Arms low, shoulders loose, cadence steady. This run is about adaptation, not speed.",
        },
        CROSS_TRAINING_GUIDANCE,
      ],
      recovery:
        "Start mobility work this week if you haven't already. 5 to 10 minutes of dynamic stretching before runs and static stretching after will pay off later. Sleep 7 to 9 hours. Your connective tissue adapts slower than your cardio system so protect it now.",
    },
    Intense: {
      title: "Discipline starts here",
      body: [
        "Don't treat this as a throwaway week. Running easy days truly easy is a skill, and it's one of the most important ones in training. Your aerobic base is the foundation everything else is built on.",
        "Quality work starts next week and you'll want fresh legs to nail it. That means this week needs to stay controlled, even if it feels too comfortable.",
      ],
      guidance: [
        {
          match: "EASY_RUN",
          text: "Easy Run: RPE 3 to 4 out of 10. Heart rate zone 1 to 2 if you use a monitor. You should be able to sing, not just speak. If that feels too slow, you're thinking about it right.",
        },
        {
          match: "LONG_OR_SAT",
          text: "Long Run: Same easy effort. Focus on smooth form rather than pace. Good time to practice race day nutrition if your plan includes fueling.",
        },
        CROSS_TRAINING_GUIDANCE,
      ],
      recovery:
        "Elite athletes spend 80% of training at easy effort. This week is a good reminder of why. Aim for 8 or more hours of sleep. Start paying attention to how your legs feel each morning. Tracking that over time helps you catch overtraining early.",
    },
  },

  BUILD: {
    Easy: {
      title: "Building your base",
      body: [
        "Each build week adds a little more than the last. The increases are small on purpose. Consistent gradual progress beats big jumps every time, and your body needs time to absorb each new level before moving to the next.",
        "Keep every run feeling comfortable. If a run feels hard, you're going too fast. Easy effort now builds the aerobic engine that will carry you to the finish line.",
      ],
      guidance: [
        {
          match: "EASY_RUN",
          text: "Easy Run: Same conversational pace as last week. RPE 3 to 4 out of 10. The distance is slightly longer but the effort stays the same.",
        },
        {
          match: "WALK",
          text: "Walk: Brisk and comfortable. Think of it as active recovery, not a workout.",
        },
        CROSS_TRAINING_GUIDANCE,
      ],
      recovery:
        "Your body does its adapting between runs, not during them. Sleep, nutrition, and rest days are part of the training. Don't skip them. If your legs feel heavy going into a run, slow down a bit more than usual.",
    },
    Moderate: {
      title: "Steady progress",
      body: [
        "Build weeks are where fitness actually gets made. The work is straightforward: run the easy days easy, and execute the quality sessions with focus. Nail those two things consistently and the fitness will come.",
        "Resist the urge to turn easy runs into moderate runs. The gap between easy and quality sessions is what makes the quality sessions work.",
      ],
      guidance: [
        {
          match: "EASY_RUN",
          text: "Easy Run: Conversational pace, RPE 4 out of 10. These runs support recovery and build aerobic base. They should never feel like a workout.",
        },
        {
          match: "TEMPO",
          text: "Tempo Run: Comfortably hard. You can speak in short phrases but not full sentences. RPE 6 to 7 out of 10. Think of it as a pace you could hold for about an hour if you had to.",
        },
        {
          match: "INTERVAL",
          text: "Intervals: Hard effort with recovery in between. RPE 7 to 8 out of 10 on the hard portions. Focus on running tall and relaxed even when it gets uncomfortable.",
        },
        {
          match: "LONG_OR_SAT",
          text: "Long Run: Easy effort throughout. If you have a GPS watch, ignore your pace and run by feel.",
        },
        CROSS_TRAINING_GUIDANCE,
      ],
      recovery:
        "Eat well on your harder days. Your body needs carbohydrates to fuel quality sessions and protein to rebuild after them. If you feel unusually fatigued heading into a build week, it's okay to dial back slightly. Fitness built on tired legs is fragile.",
    },
    Challenging: {
      title: "Do the work",
      body: [
        "These are the weeks that matter most. Build weeks in a Challenging plan require focus and honest effort. The tempo runs should be genuinely uncomfortable, the intervals should push you, and the long run should leave you tired but not wrecked.",
        "The goal is to finish each week knowing you did the work without digging so deep that recovery takes more than a day or two.",
      ],
      guidance: [
        {
          match: "EASY_RUN",
          text: "Easy Run: RPE 4 out of 10. These are not junk miles. They build aerobic capacity and help you recover between harder sessions. Run them slower than feels necessary.",
        },
        {
          match: "TEMPO",
          text: "Tempo Run: RPE 7 out of 10. Comfortably hard. You can push out a few words but conversation is difficult. Your breathing should be rhythmic and controlled, not ragged.",
        },
        {
          match: "INTERVAL",
          text: "Intervals: RPE 8 out of 10 on the hard efforts. Strong but not all-out. Focus on consistent splits rather than going out too hard and fading. Recovery between efforts should be easy jogging, not walking.",
        },
        {
          match: "LONG_OR_SAT",
          text: "Long Run: RPE 5 out of 10. Controlled and steady. The last few miles should feel challenging but manageable. If you're struggling early, slow down.",
        },
        CROSS_TRAINING_GUIDANCE,
      ],
      recovery:
        "Sleep is where the adaptation happens. Cutting sleep short cuts your fitness gains. Prioritize 7 to 9 hours. On your harder days, eat a proper meal within an hour of finishing. Foam rolling and easy walks on rest days will keep your legs fresher than full couch rest.",
    },
    Intense: {
      title: "Execute with intent",
      body: [
        "Every session in a build week has a purpose. Easy runs protect your recovery. Tempo runs build lactate threshold. Intervals develop speed and efficiency. Long runs build endurance. When you understand why you're doing each session, you run them better.",
        "The biggest mistake at this intensity is running easy days too hard. It blunts the quality sessions and accumulates fatigue faster than your body can handle. Protect your easy days fiercely.",
      ],
      guidance: [
        {
          match: "EASY_RUN",
          text: "Easy Run: RPE 3 to 4 out of 10. Heart rate zone 1 to 2. These runs are non-negotiable recovery tools. Run them slower than your ego wants.",
        },
        {
          match: "TEMPO",
          text: "Tempo Run: RPE 7 to 8 out of 10. Sustained hard effort. Your breathing is labored but controlled. You can manage a word or two but not a sentence. Hold this effort steadily rather than surging and fading.",
        },
        {
          match: "INTERVAL",
          text: "Intervals: RPE 8 to 9 out of 10 on hard efforts. Near maximum controlled speed. Focus on form when it gets hard. Recovery between reps should be slow enough that you can actually recover before the next one.",
        },
        {
          match: "LONG_OR_SAT",
          text: "Long Run: RPE 5 to 6 out of 10. Progressive effort is fine but don't race it. The long run builds the aerobic foundation that makes everything else possible.",
        },
        CROSS_TRAINING_GUIDANCE,
      ],
      recovery:
        "At this intensity, recovery is training. Sleep 8 or more hours. Eat enough to support the load you're putting on your body. If your resting heart rate is elevated in the morning or you feel flat going into a session, take it as a signal to back off. Chronic fatigue at this level builds slowly and is hard to dig out of.",
    },
  },

  CUTBACK: {
    Easy: {
      title: "Pull back and absorb",
      body: [
        "Cutback weeks are built into this plan on purpose. Less mileage this week is not a step backward, it's how your body locks in the fitness you've been building. Adaptation happens during recovery, not during the hard work.",
        "Enjoy the lighter load. Come back next week feeling fresher and stronger than before.",
      ],
      guidance: [
        {
          match: "EASY_RUN",
          text: "Easy Run: Same easy effort as always, just shorter. RPE 3 to 4 out of 10. Enjoy having a little less on your plate this week.",
        },
        {
          match: "WALK",
          text: "Walk: Same brisk comfortable pace. These feel especially good on cutback weeks.",
        },
        CROSS_TRAINING_GUIDANCE,
      ],
      recovery:
        "Use the extra time and energy to focus on sleep and nutrition. Get to bed early, eat well, and let your body do its repair work. Don't fill the recovered time with other intense physical activity.",
    },
    Moderate: {
      title: "Recovery is part of the plan",
      body: [
        "Every third or fourth week your body needs a reset. Cutback weeks reduce the load so your muscles, tendons, and nervous system can fully absorb recent training. Skipping or ignoring a cutback week is one of the most common ways runners get injured or burn out.",
        "You might feel restless or like you're losing fitness. You're not. You're gaining it.",
      ],
      guidance: [
        {
          match: "EASY_RUN",
          text: "Easy Run: RPE 3 to 4 out of 10. Even easier than usual. Think of these as shakeout runs.",
        },
        {
          match: "TEMPO",
          text: "Tempo Run: One quality session this week instead of two. Shorter than usual. Keep the effort honest but don't push into new territory.",
        },
        {
          match: "LONG_OR_SAT",
          text: "Long Run: Noticeably shorter than recent weeks. Easy effort throughout. No heroics.",
        },
        CROSS_TRAINING_GUIDANCE,
      ],
      recovery:
        "This is a good week to catch up on sleep debt, address any niggles with a sports massage or extra stretching, and make sure your nutrition is dialed in. Come into next week's build feeling genuinely refreshed.",
    },
    Challenging: {
      title: "Back off to come back stronger",
      body: [
        "Cutback weeks in a Challenging plan are not easy weeks. They're strategic recovery weeks. The mileage drops but your body is still processing the fitness gains from recent build weeks. Trust the process and resist the urge to add extra miles.",
        "One quality session this week instead of two. That's intentional. Let your body consolidate the work you've done.",
      ],
      guidance: [
        {
          match: "EASY_RUN",
          text: "Easy Run: RPE 4 out of 10. Genuinely easy. Your easy runs in build weeks might have crept up in effort without you noticing. Use this week to reset that baseline.",
        },
        {
          match: "TEMPO",
          text: "Tempo Run: The one quality session this week. Shorter than usual and capped intentionally. Treat it as a maintenance effort, not a peak performance.",
        },
        {
          match: "LONG_OR_SAT",
          text: "Long Run: Meaningfully shorter than your recent long runs. Easy effort. Focus on form and breathing rather than covering ground.",
        },
        CROSS_TRAINING_GUIDANCE,
      ],
      recovery:
        "Address any tightness or soreness this week while the load is lighter. Extra foam rolling, stretching, or a sports massage will pay dividends in the next build block. Protect your sleep and don't let the lighter schedule tempt you into late nights.",
    },
    Intense: {
      title: "Strategic recovery",
      body: [
        "At this training load, cutback weeks are essential, not optional. Your nervous system, muscles, and connective tissue accumulate stress faster than they can repair at Intense intensity. This week gives everything a chance to catch up.",
        "The runners who respect cutback weeks come out of them flying. The ones who push through them end up injured or burned out two blocks later.",
      ],
      guidance: [
        {
          match: "EASY_RUN",
          text: "Easy Run: RPE 3 to 4 out of 10. Zone 1 to 2 heart rate. These should feel almost too easy. That's the point.",
        },
        {
          match: "TEMPO",
          text: "Tempo Run: One quality session this week. Shorter and capped. Don't try to make it a peak effort just because you feel fresh. Save that energy for next week.",
        },
        {
          match: "LONG_OR_SAT",
          text: "Long Run: Shorter than recent weeks. Easy controlled effort. No surging in the final miles.",
        },
        CROSS_TRAINING_GUIDANCE,
      ],
      recovery:
        "Use this week to audit how your body is feeling. Any persistent soreness, tightness, or fatigue that you've been running through deserves attention now while the load is light. Sleep 8 or more hours. Eat enough to support recovery even though the mileage is down. Your next build block will be your strongest yet if you respect this week.",
    },
  },

  PEAK: {
    Easy: {
      title: "Your biggest week",
      body: [
        "This is the highest mileage week of your plan. It's also the last big push before you start tapering toward race day. The goal this week is simple: complete the work, keep the effort easy, and trust that the fitness is there.",
        "Don't be surprised if you feel tired by the end of the week. That's normal. The taper that follows is designed to bring you back to feeling fresh and ready.",
      ],
      guidance: [
        {
          match: "EASY_RUN",
          text: "Easy Run: RPE 3 to 4 out of 10. Same easy conversational effort as always. The distance might be longer than you're used to but the effort stays the same.",
        },
        CROSS_TRAINING_GUIDANCE,
      ],
      recovery:
        "Prioritize sleep every night this week. Eat well before and after your longer sessions. Your body is working hard and needs the fuel. By the end of this week you might feel more fatigued than usual. That's expected and it means the taper ahead will feel amazing.",
    },
    Moderate: {
      title: "Your highest week, your best preparation",
      body: [
        "Peak week is the culmination of all your build weeks. The mileage is at its highest and the quality sessions are at their most demanding. Completing this week well gives you the confidence and fitness to race your best.",
        "Execute each session with focus. Easy runs easy, quality sessions with purpose, and the long run steady and controlled from start to finish.",
      ],
      guidance: [
        {
          match: "EASY_RUN",
          text: "Easy Run: RPE 4 out of 10. Even in peak week, easy means easy. These runs support recovery between harder sessions.",
        },
        {
          match: "TEMPO",
          text: "Tempo Run: Your best sustained effort of the plan. RPE 7 out of 10. Hold back slightly in the first half and let the second half feel strong.",
        },
        {
          match: "LONG_OR_SAT",
          text: "Long Run: The longest run of your plan. Start conservatively. The goal is to finish feeling like you executed well, not like you survived.",
        },
        CROSS_TRAINING_GUIDANCE,
      ],
      recovery:
        "Sleep is critical this week. Aim for 8 hours every night. Eat a proper meal within an hour of every run. By the end of the week some fatigue is normal and expected. The taper ahead is specifically designed to bring you back to peak freshness for race day.",
    },
    Challenging: {
      title: "Leave it all on the training field",
      body: [
        "This is your biggest week. The fitness you've built over this entire plan comes together here. Peak week in a Challenging plan asks a lot of you and delivering on it is one of the most satisfying things in running training.",
        "Execute each session with intention. The tempo should be genuinely hard. The intervals should push your limits. The long run should leave you tired but proud. Then rest, because the taper is next.",
      ],
      guidance: [
        {
          match: "EASY_RUN",
          text: "Easy Run: RPE 4 out of 10. Don't let the excitement of peak week turn your easy runs into moderate runs. Protect your recovery between quality sessions.",
        },
        {
          match: "TEMPO",
          text: "Tempo Run: RPE 7 to 8 out of 10. Your strongest tempo of the plan. Warm up properly, hold back in the first mile, and build into it. Controlled discomfort is the target.",
        },
        {
          match: "INTERVAL",
          text: "Intervals: RPE 8 to 9 out of 10 on the hard efforts. Push the pace but keep your form together. Strong turnover, relaxed shoulders, eyes forward.",
        },
        {
          match: "LONG_OR_SAT",
          text: "Long Run: Start easy and stay steady. The back half will test your mental strength as much as your physical fitness. Embrace it.",
        },
        CROSS_TRAINING_GUIDANCE,
      ],
      recovery:
        "This week demands more from your body than any other. Sleep 8 or more hours every night. Fuel your harder sessions with carbohydrates beforehand and protein afterward. A sports massage or extra foam rolling midweek can help you get through the back half of the week feeling better.",
    },
    Intense: {
      title: "The week you trained for",
      body: [
        "Peak week at Intense is genuinely hard. It's supposed to be. This week represents the full expression of your training and completing it well is one of the best indicators of race readiness.",
        "Every session matters this week. Execute your easy runs at easy effort so your quality sessions land with full force. Don't leave your race in the training week.",
      ],
      guidance: [
        {
          match: "EASY_RUN",
          text: "Easy Run: RPE 3 to 4 out of 10. Zone 1 to 2 heart rate. Protect these with everything you have. Running them too hard is the single most common mistake in peak week.",
        },
        {
          match: "TEMPO",
          text: "Tempo Run: RPE 8 out of 10. Your peak tempo effort. Warm up thoroughly. The first mile should feel controlled. Let the middle miles find their rhythm. Finish strong but not desperate.",
        },
        {
          match: "INTERVAL",
          text: "Intervals: RPE 8 to 9 out of 10. Near race pace or faster depending on your distance. Focus on consistent reps rather than going out hard and dying. Recovery between reps is important, take it fully.",
        },
        {
          match: "LONG_OR_SAT",
          text: "Long Run: RPE 5 to 6 out of 10. Long and steady. You've done this before. Trust your preparation and run within yourself. The taper starts after this.",
        },
        CROSS_TRAINING_GUIDANCE,
      ],
      recovery:
        "Sleep 8 or more hours every night this week without compromise. Fuel aggressively before and after hard sessions. Monitor your resting heart rate and morning energy levels. If you feel unusually flat or run down midweek, dial back one session rather than pushing through. Arriving at the taper healthy matters more than one perfect peak week session.",
    },
  },

  TAPER: {
    Easy: {
      title: "Trust the work you've done",
      body: [
        "The hard work is behind you. This week your mileage drops and that's completely intentional. Your body is storing energy, repairing muscle, and getting ready to perform on race day.",
        "You might feel restless or like you're losing fitness. You're not. The fitness is locked in. What you're doing now is unwrapping it.",
      ],
      guidance: [
        {
          match: "EASY_RUN",
          text: "Easy Run: Short and easy. RPE 3 out of 10. These runs keep your legs moving without adding any fatigue. Don't try to squeeze in extra miles.",
        },
        CROSS_TRAINING_GUIDANCE,
      ],
      recovery:
        "Sleep well, eat well, and stay off your feet when you can. Avoid the temptation to do extra walking, hiking, or other activity to fill the time. Your one job this week is to arrive at the start line feeling fresh.",
    },
    Moderate: {
      title: "Less is more",
      body: [
        "Taper week can feel uncomfortable for runners who are used to training hard. The drop in mileage is real and intentional. Your body needs this time to consolidate fitness, top up glycogen stores, and repair the small damage that accumulates during training.",
        "Resist every urge to add extra runs or push harder than prescribed. The hay is in the barn. Your job now is to show up rested.",
      ],
      guidance: [
        {
          match: "EASY_RUN",
          text: "Easy Run: RPE 3 to 4 out of 10. Short and relaxed. These are maintenance runs, not fitness builders.",
        },
        {
          match: "TEMPO",
          text: "Tempo Run: A short quality session to keep your legs sharp. Don't treat it as a final test or a chance to prove fitness. Keep it controlled and finish feeling good.",
        },
        {
          match: "LONG_OR_SAT",
          text: "Long Run: Noticeably shorter than peak week. Easy effort. Focus on smooth relaxed running rather than covering ground.",
        },
        CROSS_TRAINING_GUIDANCE,
      ],
      recovery:
        "Sleep is your most important training tool this week. Aim for 8 or more hours every night. Eat well and stay hydrated. Some runners feel sluggish or heavy during taper. That's normal and temporary. You'll feel much better on race morning.",
    },
    Challenging: {
      title: "Sharpen, don't grind",
      body: [
        "Taper week for a Challenging plan is about maintaining sharpness without accumulating any new fatigue. The volume drops significantly but a short quality session keeps your legs feeling snappy rather than flat.",
        "The biggest mistake in taper week is trying to cram in extra work because you're nervous. Don't do it. Every extra mile you run this week is borrowed from race day.",
      ],
      guidance: [
        {
          match: "EASY_RUN",
          text: "Easy Run: RPE 4 out of 10. Short and controlled. Your legs should start feeling fresher as the week goes on. That's the taper working.",
        },
        {
          match: "TEMPO",
          text: "Tempo Run: A brief sharpener. Keep it short and purposeful. RPE 6 to 7 out of 10. Finish feeling like you had more in the tank.",
        },
        {
          match: "LONG_OR_SAT",
          text: "Long Run: Much shorter than peak week. Easy and relaxed. Focus on smooth form and easy breathing. Enjoy having less on your plate.",
        },
        CROSS_TRAINING_GUIDANCE,
      ],
      recovery:
        "Stay disciplined about sleep and nutrition this week. Carbohydrate intake can increase slightly as you approach race day to top up your glycogen stores. Avoid anything new this week including new foods, new shoes, or new stretching routines. Race week starts next and you want to arrive there healthy and confident.",
    },
    Intense: {
      title: "The work is done",
      body: [
        "At Intense training load, taper week is genuinely important for performance. The accumulated fatigue from weeks of hard training needs time to clear before race day. Cutting taper short or filling it with extra work is one of the most reliable ways to underperform on race day.",
        "Trust the process. The fitness you built during peak week doesn't disappear in a few easy days. It consolidates.",
      ],
      guidance: [
        {
          match: "EASY_RUN",
          text: "Easy Run: RPE 3 to 4 out of 10. Zone 1 heart rate if you use a monitor. These should feel almost effortless. If they do, that's the taper working exactly as intended.",
        },
        {
          match: "TEMPO",
          text: "Tempo Run: Short and sharp. RPE 7 out of 10. Just enough to remind your legs what fast feels like without digging into your recovery. Finish feeling strong and controlled.",
        },
        {
          match: "LONG_OR_SAT",
          text: "Long Run: Significantly shorter than peak week. Easy effort. Smooth and relaxed. Let yourself enjoy running without the pressure of a big session.",
        },
        CROSS_TRAINING_GUIDANCE,
      ],
      recovery:
        "Sleep 8 or more hours every night. Eat well and stay hydrated. Some runners experience taper tantrums this week, feeling anxious, heavy, or like their fitness has disappeared. It hasn't. Those feelings are a normal part of the taper process and a sign your body is doing exactly what it should. Trust your training and stay calm.",
    },
  },

  TAPER_2: {
    Moderate: {
      title: "Final preparations",
      body: [
        "You're nearly there. Taper 2 brings the mileage to its lowest point of the plan and your legs should be feeling noticeably fresher than they have in weeks. That freshness is your fitness ready to perform.",
        "Keep the week calm and consistent. Stick to the plan and don't add anything extra.",
      ],
      guidance: [
        {
          match: "EASY_RUN",
          text: "Easy Run: RPE 3 out of 10. Very short and very relaxed. These keep your legs ticking over without adding any fatigue.",
        },
        {
          match: "TEMPO",
          text: "Tempo Run: A brief final sharpener. Very short and controlled. RPE 6 out of 10. Just enough to remind your legs what quality effort feels like.",
        },
        {
          match: "LONG_OR_SAT",
          text: "Long Run: Short and easy. The last longer effort before race day. Keep it relaxed and finish feeling good.",
        },
        CROSS_TRAINING_GUIDANCE,
      ],
      recovery:
        "Sleep well and eat well. Increase carbohydrates slightly in the final two days before your race to top up your glycogen stores. Lay out your race kit, plan your travel, and sort your logistics so race morning is calm. The less you have to think about on race day the better you'll run.",
    },
    Challenging: {
      title: "Sharp and ready",
      body: [
        "By now your legs should be feeling significantly better than they did at peak week. The fatigue has cleared and your fitness is right at the surface ready to go. Taper 2 keeps the volume low while one short quality session keeps you sharp.",
        "You've done the work. Now trust it.",
      ],
      guidance: [
        {
          match: "EASY_RUN",
          text: "Easy Run: RPE 3 to 4 out of 10. Short and easy. Your legs should feel good. Enjoy it.",
        },
        {
          match: "TEMPO",
          text: "Tempo Run: Very short final sharpener. RPE 6 to 7 out of 10. Keep it brief and controlled. Finish feeling fast and ready rather than worked.",
        },
        {
          match: "LONG_OR_SAT",
          text: "Long Run: Short and relaxed. The last run of any real length before race day. Easy effort throughout.",
        },
        CROSS_TRAINING_GUIDANCE,
      ],
      recovery:
        "Carb load lightly in the final two days before your race. Nothing excessive but nudging your carbohydrate intake up will top off your glycogen stores. Sleep well every night. Stay calm and confident. Everything you've done over this training block has prepared you for what's ahead.",
    },
  },

  RACE_WEEK: {
    Easy: {
      title: "Race week is here",
      body: [
        "You made it. Everything you've done over these past weeks has prepared you for this. Race week is not a training week. Your only job is to stay healthy, stay relaxed, and show up on race day feeling ready.",
        "The hay is in the barn. Nothing you do this week will make you fitter. But resting well, eating well, and staying calm will absolutely help you perform better.",
      ],
      guidance: [
        {
          match: "EASY_RUN",
          text: "Easy Run: Short and easy. Just enough to keep your legs moving. RPE 3 out of 10. Don't push, don't test yourself, just shake out the legs.",
        },
        {
          match: "SHAKEOUT",
          text: "Shakeout Run: Very short and very easy. This primes your legs without adding any fatigue.",
        },
      ],
      recovery:
        "Stay off your feet as much as you can. Avoid long walks, standing for hours, or anything that adds unnecessary leg fatigue. Sleep as well as you can the two nights before the race. Eat familiar foods and stay hydrated. On race morning, eat what you know works for you and give yourself plenty of time to get to the start line without rushing.",
    },
    Moderate: {
      title: "Time to race",
      body: [
        "Your training is complete. Race week is about one thing: arriving at the start line healthy, rested, and confident. The work is done and now you let it perform.",
        "Keep the week simple. Stick to the plan, avoid anything out of the ordinary, and trust that your preparation has you ready.",
      ],
      guidance: [
        {
          match: "EASY_RUN",
          text: "Easy Run: Short maintenance run early in the week. RPE 3 to 4 out of 10. Keep it brief and don't overthink it.",
        },
        {
          match: "SHAKEOUT",
          text: "Shakeout Run: Loose and easy. Some runners like to throw in a few short pickups at race pace to wake up the legs. If that feels good, go for it.",
        },
      ],
      recovery:
        "Avoid long walks and time on your feet. Sleep well the two nights before your race. Eat familiar carbohydrate-rich meals the day before without overeating. Hydrate consistently throughout the week rather than trying to tank up the night before. Lay out your gear the night before so race morning is calm and unhurried.",
    },
    Challenging: {
      title: "Execute the plan you trained for",
      body: [
        "Race week is the final chapter of a demanding training block. You've done the work. Now the goal is to protect that work by staying disciplined, staying rested, and showing up on race day with fresh legs and a clear head.",
        "Confidence comes from preparation and you have plenty of it. Trust your training.",
      ],
      guidance: [
        {
          match: "EASY_RUN",
          text: "Easy Run: Short and controlled early in the week. RPE 4 out of 10. Keep it brief. This is not the time for bonus miles or extra effort.",
        },
        {
          match: "SHAKEOUT",
          text: "Shakeout Run: A few short strides at race pace are fine if it helps you feel sharp. Finish feeling loose and ready.",
        },
      ],
      raceDay:
        "Start conservatively. The biggest mistake in a race is going out too fast in the first mile. Run the first third at a pace that feels almost too easy, settle into goal pace in the middle, and leave whatever you have left for the final push. Fuel and hydrate according to your plan, not on the fly.",
      recovery:
        "Stay off your feet, sleep well, and eat familiar foods. Nothing new this week including shoes, clothing, or pre-race meals. Trust your preparation and keep your mind calm. Nerves are normal and a sign that you care. Channel them into focus rather than anxiety.",
    },
    Intense: {
      title: "Arrive ready, race smart",
      body: [
        "You've put in serious work to get here. Race week at Intense level means you're arriving with a high level of fitness and a body that's ready to perform. The only thing left to do is protect that fitness, stay sharp, and execute on race day.",
        "The training is complete. This week is about management, not improvement.",
      ],
      guidance: [
        {
          match: "EASY_RUN",
          text: "Easy Run: Very short and very easy early in the week. RPE 3 out of 10. Zone 1 heart rate. Treat it as a formality to keep the legs moving, nothing more.",
        },
        {
          match: "SHAKEOUT",
          text: "Shakeout Run: Easy jog with a few short strides at race pace if you want to feel sharp. Keep the total time brief and finish feeling good.",
        },
      ],
      raceDay:
        "You know how to race. Start controlled, settle into your rhythm, and build as the race goes on. In the final third, run on feel rather than pace. Your training has prepared you for discomfort. When it gets hard, lean into it rather than backing off. That's what Intense training builds.",
      recovery:
        "Sleep 8 hours the two nights before your race. Eat familiar carbohydrate-rich meals. Hydrate well throughout the week. Avoid alcohol, unusual foods, and extra physical activity. Lay out your race kit the night before. On race morning keep your warmup routine familiar and give yourself enough time to feel relaxed at the start line.",
    },
  },
};
